import { fontThemeStyle } from '@src/components/Typography/theme';
import { getIsNewThemeActive, themeStyle } from '@src/theming';

import { ButtonTheme } from './types';

const isNewThemeActive = getIsNewThemeActive();

const baseTheme: ButtonTheme = {
  height: '40px',
  disabledColor: themeStyle.inkMedium,
  backgroundDisabledColor: themeStyle.inkLight,
  borderDisabledColor: themeStyle.inkLight,

  disabledColorDark: themeStyle.darkInkMedium,
  backgroundDisabledColorDark: themeStyle.darkInkSuperLight,
  borderDisabledColorDark: themeStyle.transparent,

  fontSize: themeStyle.fontSize14,
  fontWeight: isNewThemeActive
    ? fontThemeStyle.buttonFontWeight
    : themeStyle.fontSemiBold,
  lineHeight: '18px',
  iconHeight: '16px',
  textDecoration: 'none',

  borderRadius: themeStyle.radiusMedium,
  padding: '16px',
  iconMinWidth: '0px',
};

export const primaryTheme: ButtonTheme = {
  ...baseTheme,
  name: 'primary',

  color: themeStyle.varInkBase,
  spinnerColor: themeStyle.varInkBase,
  activeColor: themeStyle.varInkBase,

  backgroundColor: themeStyle.brandMain,
  borderColor: themeStyle.brandMain,
  backgroundActiveColor: themeStyle.brandMedium,
  borderActiveColor: themeStyle.brandMedium,

  backgroundColorDark: themeStyle.darkBrandMain,
  borderColorDark: themeStyle.darkBrandMain,
  backgroundActiveColorDark: themeStyle.darkBrandMedium,
  borderActiveColorDark: themeStyle.darkBrandMedium,
};

export const primaryLightTheme: ButtonTheme = {
  ...baseTheme,
  name: 'primary',

  color: themeStyle.inkBase,
  spinnerColor: themeStyle.inkBase,
  activeColor: themeStyle.inkBase,

  backgroundColor: themeStyle.brandMain,
  borderColor: themeStyle.brandMain,
  backgroundActiveColor: themeStyle.brandMedium,
  borderActiveColor: themeStyle.brandMedium,

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const primaryDarkTheme: ButtonTheme = {
  ...baseTheme,
  name: 'primaryDark',

  color: themeStyle.darkInkBase,
  spinnerColor: themeStyle.darkInkBase,
  activeColor: themeStyle.darkInkBase,

  backgroundColor: themeStyle.darkBrandMain,
  borderColor: themeStyle.darkBrandMain,
  backgroundActiveColor: themeStyle.darkBrandMain,
  borderActiveColor: themeStyle.darkBrandMain,

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const secondaryTheme: ButtonTheme = {
  ...baseTheme,
  name: 'secondary',

  color: themeStyle.varInkMain,
  spinnerColor: themeStyle.varInkMain,
  activeColor: themeStyle.varInkMain,

  backgroundColor: themeStyle.varInkLight,
  borderColor: themeStyle.transparent,
  backgroundActiveColor: themeStyle.varInkSuperLight,
  borderActiveColor: themeStyle.varInkLight,

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const secondaryLightTheme: ButtonTheme = {
  ...baseTheme,
  name: 'secondary',

  color: themeStyle.inkMain,
  spinnerColor: themeStyle.inkMain,
  activeColor: themeStyle.inkMain,

  backgroundColor: themeStyle.inkLight,
  borderColor: themeStyle.transparent,
  backgroundActiveColor: themeStyle.inkSuperLight,
  borderActiveColor: themeStyle.inkLight,

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const secondaryDarkTheme: ButtonTheme = {
  ...baseTheme,
  name: 'secondary',

  color: themeStyle.darkInkMain,
  spinnerColor: themeStyle.darkInkMain,
  activeColor: themeStyle.darkInkMain,

  backgroundColor: themeStyle.darkInkLight,
  borderColor: themeStyle.transparent,
  backgroundActiveColor: themeStyle.darkInkSuperLight,
  borderActiveColor: themeStyle.darkInkLight,

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const transparentTheme: ButtonTheme = {
  ...secondaryTheme,
  name: null,
  backgroundColor: themeStyle.transparent,
  backgroundActiveColor: themeStyle.varInkSuperLight,
  borderColor: themeStyle.transparent,
  borderActiveColor: themeStyle.transparent,
};

const smallTheme = {
  height: '30px',
  fontSize: themeStyle.fontSize12,
  lineHeight: '15px',
  iconHeight: '14px',
  padding: '14px',
};

export const primarySmallTheme: ButtonTheme = {
  ...primaryTheme,
  ...smallTheme,
};

export const primaryLightSmallTheme: ButtonTheme = {
  ...primaryLightTheme,
  ...smallTheme,
};

export const primaryDarkSmallTheme: ButtonTheme = {
  ...primaryDarkTheme,
  ...smallTheme,
};

export const secondarySmallTheme: ButtonTheme = {
  ...secondaryTheme,
  ...smallTheme,
};

export const secondaryLightSmallTheme: ButtonTheme = {
  ...secondaryLightTheme,
  ...smallTheme,
};

export const secondaryDarkSmallTheme: ButtonTheme = {
  ...secondaryDarkTheme,
  ...smallTheme,
};

const giantTheme = {
  height: '48px',
  padding: '32px',
};

export const primaryGiantTheme: ButtonTheme = {
  ...primaryTheme,
  ...giantTheme,
};

export const secondaryGiantTheme: ButtonTheme = {
  ...secondaryTheme,
  ...giantTheme,
};

export const primaryLightGiantTheme: ButtonTheme = {
  ...primaryLightTheme,
  ...giantTheme,
};

export const primaryDarkGiantTheme: ButtonTheme = {
  ...primaryDarkTheme,
  ...giantTheme,
};

export const secondaryLightGiantTheme: ButtonTheme = {
  ...secondaryLightTheme,
  ...giantTheme,
};

export const secondaryDarkGiantTheme: ButtonTheme = {
  ...secondaryDarkTheme,
  ...giantTheme,
};

export const menuTheme: ButtonTheme = {
  ...primaryTheme,
  name: null,

  height: '32px',
  color: themeStyle.varInkMedium,
  spinnerColor: themeStyle.varInkMedium,
  backgroundColor: themeStyle.transparent,
  borderColor: themeStyle.transparent,

  activeColor: themeStyle.varInkMain,
  backgroundActiveColor: themeStyle.varInkSuperLight,
  borderActiveColor: themeStyle.varInkSuperLight,

  padding: '12px',

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const menuSmallTheme: ButtonTheme = {
  ...menuTheme,
  ...smallTheme,
};

export const floatTheme: ButtonTheme = {
  ...menuTheme,
  name: 'header',

  height: '40px',
  padding: '16px',
  color: themeStyle.varInkMain,
  backgroundColor: themeStyle.varBackgroundAlt,
  borderColor: themeStyle.transparent,
  activeColor: themeStyle.varBrandMain,
  backgroundActiveColor: themeStyle.varBackgroundAlt,
  borderActiveColor: themeStyle.transparent,
  boxShadow: themeStyle.varShadowMedium,
  boxShadowActive: themeStyle.varShadowMedium,
};

export const greyTheme: ButtonTheme = {
  ...primaryTheme,
  name: null,

  color: themeStyle.varInkMain,
  spinnerColor: themeStyle.varInkMain,
  backgroundColor: themeStyle.varInkSuperLight,
  borderColor: themeStyle.varInkSuperLight,

  activeColor: themeStyle.varInkMain,
  backgroundActiveColor: themeStyle.varInkLight,
  borderActiveColor: themeStyle.varInkLight,

  padding: '12px',

  backgroundColorDark: null,
  borderColorDark: null,
  backgroundActiveColorDark: null,
  borderActiveColorDark: null,
};

export const greySmallTheme: ButtonTheme = {
  ...greyTheme,
  ...smallTheme,
};

export const editorMenuItemTheme: ButtonTheme = {
  ...menuTheme,
  name: 'editorMenuItem',

  height: '27px',
  padding: '10px',
  backgroundActiveColor: themeStyle.varBrandSuperLight,
  borderActiveColor: themeStyle.transparent,
};

export const headTabTheme = {
  name: null,

  backgroundColor: themeStyle.transparent,
  backgroundColorDark: themeStyle.transparent,
  backgroundActiveColor: themeStyle.transparent,
  backgroundActiveColorDark: themeStyle.transparent,
  borderColor: themeStyle.varInkLight,
  borderColorDark: themeStyle.varInkLight,
  borderActiveColor: themeStyle.varInkMain,
  borderActiveColorDark: themeStyle.varInkMain,
  color: themeStyle.varInkMedium,
  activeColor: themeStyle.varInkMain,
  borderRadius: '0px',
  borderBottom: true,
};
